// Machine Management Component per ORIGINAL_REQUEST § R2 & PROJECT.md F2.1
import React, { useState, useEffect } from 'react';
import type { Machine, ChecklistTemplate, MachineStatus } from '../../types';
import {
  getAllMachines,
  saveMachine,
  deleteMachine,
  updateMachineHours,
  getAllTemplates,
} from '../../db';
import { Button, Card, Modal } from '../common';
import {
  Cpu,
  PlusCircle,
  Pencil,
  Trash2,
  Clock,
  Search,
  CheckCircle2,
  AlertCircle,
  Layers,
  MapPin,
  Barcode,
} from 'lucide-react';
import {
  broadcastSystemData,
  subscribeSystemDataUpdates,
} from '../../services/config/cloudConfigService';

export const MachineManager: React.FC = () => {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingMachine, setEditingMachine] = useState<Machine | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [quickHoursMachine, setQuickHoursMachine] = useState<Machine | null>(null);
  const [quickHoursToAdd, setQuickHoursToAdd] = useState<string>('8');

  // Form fields
  const [formId, setFormId] = useState<string>('');
  const [formName, setFormName] = useState<string>('');
  const [formModel, setFormModel] = useState<string>('');
  const [formSerialNo, setFormSerialNo] = useState<string>('');
  const [formLocation, setFormLocation] = useState<string>('');
  const [formAssignedTemplateId, setFormAssignedTemplateId] = useState<string>('');
  const [formCumulativeHours, setFormCumulativeHours] = useState<string>('0');
  const [formStatus, setFormStatus] = useState<MachineStatus>('NORMAL');
  const [formError, setFormError] = useState<string>('');
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string>('');

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [machineList, templateList] = await Promise.all([
        getAllMachines(),
        getAllTemplates(),
      ]);
      setMachines(machineList);
      setTemplates(templateList);
    } catch (err) {
      console.error('[MachineManager] Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const unsubscribe = subscribeSystemDataUpdates(() => {
      loadData();
    });
    return unsubscribe;
  }, []);

  const openAddModal = () => {
    setEditingMachine(null);
    setFormId(`CNC-${String(machines.length + 1).padStart(2, '0')}`);
    setFormName('');
    setFormModel('');
    setFormSerialNo('');
    setFormLocation('Workshop Bay A');
    setFormAssignedTemplateId(templates[0]?.id || 'tpl_tos_whnq1315');
    setFormCumulativeHours('0');
    setFormStatus('NORMAL');
    setFormError('');
    setIsModalOpen(true);
  };

  const openEditModal = (machine: Machine) => {
    setEditingMachine(machine);
    setFormId(machine.id);
    setFormName(machine.name);
    setFormModel(machine.model);
    setFormSerialNo(machine.serialNo);
    setFormLocation(machine.location || '');
    setFormAssignedTemplateId(machine.assignedTemplateId);
    setFormCumulativeHours(String(machine.cumulativeHours));
    setFormStatus(machine.status);
    setFormError('');
    setIsModalOpen(true);
  };

  const handleSaveMachine = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formId.trim()) {
      setFormError('กรุณาระบุรหัสเครื่องจักร (Machine ID)');
      return;
    }
    if (!formName.trim()) {
      setFormError('กรุณาระบุชื่อเครื่องจักร (Machine Name)');
      return;
    }
    if (!formModel.trim()) {
      setFormError('กรุณาระบุรุ่นเครื่องจักร (Model)');
      return;
    }
    if (!formSerialNo.trim()) {
      setFormError('กรุณาระบุหมายเลขซีเรียล (Serial No.)');
      return;
    }
    if (!formAssignedTemplateId.trim()) {
      setFormError('กรุณาเลือกเทมเพลตที่กำหนด (Assigned Template)');
      return;
    }

    const hours = parseFloat(formCumulativeHours);
    if (isNaN(hours) || hours < 0) {
      setFormError('ชั่วโมงทำงานสะสมต้องเป็นตัวเลขที่ไม่ติดลบ');
      return;
    }

    const now = new Date().toISOString();
    const machineToSave: Machine = {
      id: formId.trim(),
      name: formName.trim(),
      model: formModel.trim(),
      serialNo: formSerialNo.trim(),
      location: formLocation.trim() || undefined,
      assignedTemplateId: formAssignedTemplateId.trim(),
      cumulativeHours: hours,
      status: formStatus,
      createdAt: editingMachine?.createdAt || now,
      updatedAt: now,
      lastInspectionDate: editingMachine?.lastInspectionDate,
      lastInspectionHours: editingMachine?.lastInspectionHours,
    };

    try {
      await saveMachine(machineToSave);
      await loadData();
      setIsModalOpen(false);

      // Broadcast centralized machine update to all devices
      broadcastSystemData({
        reason: editingMachine
          ? `อัปเดตเครื่องจักร ${machineToSave.name}`
          : `เพิ่มเครื่องจักร ${machineToSave.name}`,
      }).catch(() => {});

      setSaveSuccessMsg(
        editingMachine
          ? `อัปเดตและกระจายข้อมูลเครื่องจักร "${machineToSave.name}" ไปยังทุกเครื่องเรียบร้อยแล้ว`
          : `เพิ่มและกระจายเครื่องจักร "${machineToSave.name}" ไปยังทุกเครื่องเรียบร้อยแล้ว`
      );
      setTimeout(() => setSaveSuccessMsg(''), 4000);
    } catch (err: any) {
      setFormError(`บันทึกไม่สำเร็จ: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDeleteMachine = async (id: string) => {
    try {
      await deleteMachine(id);
      setDeleteConfirmId(null);
      await loadData();

      // Broadcast machine deletion to all devices
      broadcastSystemData({ reason: `ลบเครื่องจักร ${id}` }).catch(() => {});

      setSaveSuccessMsg(`ลบและกระจายการเปลี่ยนแปลงเครื่องจักร ${id} ไปยังทุกเครื่องเรียบร้อยแล้ว`);
      setTimeout(() => setSaveSuccessMsg(''), 4000);
    } catch (err) {
      console.error('[MachineManager] Delete failed:', err);
    }
  };

  const handleQuickAddHours = async () => {
    if (!quickHoursMachine) return;
    const add = parseFloat(quickHoursToAdd);
    if (isNaN(add) || add <= 0) return;

    try {
      await updateMachineHours(
        quickHoursMachine.id,
        quickHoursMachine.cumulativeHours + add
      );
      const machineId = quickHoursMachine.id;
      setQuickHoursMachine(null);
      setQuickHoursToAdd('8');
      await loadData();

      // Broadcast updated operating hours to all devices
      broadcastSystemData({ reason: `อัปเดตชั่วโมงทำงาน ${machineId} (+${add}h)` }).catch(() => {});

      setSaveSuccessMsg(`อัปเดตชั่วโมงทำงานเครื่อง ${machineId} และกระจายข้อมูลเรียบร้อยแล้ว`);
      setTimeout(() => setSaveSuccessMsg(''), 4000);
    } catch (err) {
      console.error('[MachineManager] Update hours failed:', err);
    }
  };

  const filteredMachines = machines.filter(
    (m) =>
      m.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.serialNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: MachineStatus) => {
    switch (status) {
      case 'NORMAL':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5" />
            ปกติ (Up-to-Date)
          </span>
        );
      case 'DUE_SOON':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <Clock className="w-3.5 h-3.5" />
            ใกล้ครบกำหนด (Due Soon)
          </span>
        );
      case 'OVERDUE':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertCircle className="w-3.5 h-3.5" />
            เกินกำหนด (Overdue)
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Top Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Cpu className="w-6 h-6 text-blue-600" />
            จัดการโปรไฟล์เครื่องจักร (Machine Management)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            เพิ่ม แก้ไข หรือลบข้อมูลเครื่องจักร CNC พร้อมผูกเทมเพลตและติดตามชั่วโมงทำงาน (เชื่อมต่อระบบรวมศูนย์อัตโนมัติ)
          </p>
        </div>

        <Button
          variant="primary"
          onClick={openAddModal}
          leftIcon={<PlusCircle className="w-5 h-5" />}
        >
          เพิ่มเครื่องจักรใหม่
        </Button>
      </div>

      {saveSuccessMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-sm flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>{saveSuccessMsg}</span>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="ค้นหาด้วยรหัสเครื่อง, ชื่อ, รุ่น, หรือ Serial No..."
          className="w-full bg-white border border-slate-300 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[48px]"
        />
      </div>

      {/* Machine Cards List */}
      {isLoading ? (
        <div className="p-12 text-center text-slate-500">กำลังโหลดรายการเครื่องจักร...</div>
      ) : filteredMachines.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500 shadow-xs">
          <Cpu className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-base font-semibold text-slate-800">ไม่พบข้อมูลเครื่องจักร</p>
          <p className="text-xs text-slate-500 mt-1">
            {searchTerm ? 'ไม่พบเครื่องจักรที่ตรงกับคำค้นหา' : 'ยังไม่มีเครื่องจักรในระบบ คลิกปุ่มด้านบนเพื่อเพิ่ม'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredMachines.map((m) => {
            const templateName =
              templates.find((t) => t.id === m.assignedTemplateId)?.name ||
              m.assignedTemplateId;

            return (
              <div
                key={m.id}
                className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl p-5 shadow-xs flex flex-col justify-between transition-all"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold px-2.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                          {m.id}
                        </span>
                        <h3 className="text-base font-bold text-slate-900">{m.name}</h3>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-700">Model: {m.model}</span>
                        <span>•</span>
                        <span className="font-mono text-slate-500 flex items-center gap-1">
                          <Barcode className="w-3.5 h-3.5" /> {m.serialNo}
                        </span>
                      </p>
                    </div>
                    <div>{getStatusBadge(m.status)}</div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-slate-500 block mb-0.5 flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" /> เทมเพลตที่ใช้
                      </span>
                      <span className="font-medium text-slate-800 truncate block" title={templateName}>
                        {templateName}
                      </span>
                    </div>

                    <div>
                      <span className="text-slate-500 block mb-0.5 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> ชั่วโมงทำงานสะสม
                      </span>
                      <span className="font-mono font-bold text-amber-700 text-sm">
                        {m.cumulativeHours.toLocaleString()} ชม.
                      </span>
                    </div>

                    {m.location && (
                      <div className="col-span-2">
                        <span className="text-slate-500 block mb-0.5 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> ตำแหน่งที่ตั้ง
                        </span>
                        <span className="text-slate-700">{m.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Actions */}
                <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setQuickHoursMachine(m)}
                    leftIcon={<Clock className="w-4 h-4 text-amber-600" />}
                  >
                    + ชม.
                  </Button>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditModal(m)}
                      leftIcon={<Pencil className="w-4 h-4" />}
                    >
                      แก้ไข
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleteConfirmId(m.id)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      leftIcon={<Trash2 className="w-4 h-4" />}
                    >
                      ลบ
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingMachine ? 'แก้ไขโปรไฟล์เครื่องจักร' : 'เพิ่มเครื่องจักรใหม่'}
        maxWidth="md"
      >
        <form onSubmit={handleSaveMachine} className="space-y-4">
          {formError && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                รหัสเครื่องจักร (Machine ID) *
              </label>
              <input
                type="text"
                value={formId}
                disabled={Boolean(editingMachine)}
                onChange={(e) => setFormId(e.target.value)}
                placeholder="เช่น CNC-01, TOS-01"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60 font-mono placeholder-slate-400"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                รุ่นเครื่องจักร (Model / Type) *
              </label>
              <input
                type="text"
                value={formModel}
                onChange={(e) => setFormModel(e.target.value)}
                placeholder="เช่น WHN(Q) 13/15, LB3000"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-slate-400"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              ชื่อเครื่องจักร (Machine Name) *
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="เช่น TOS VARNSDORF WHN(Q) 13/15 CNC"
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-slate-400"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                หมายเลขซีเรียล (Serial No.) *
              </label>
              <input
                type="text"
                value={formSerialNo}
                onChange={(e) => setFormSerialNo(e.target.value)}
                placeholder="เช่น WHN-2023-001"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono placeholder-slate-400"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ตำแหน่งที่ตั้ง (Location)
              </label>
              <input
                type="text"
                value={formLocation}
                onChange={(e) => setFormLocation(e.target.value)}
                placeholder="เช่น Bay A, โรงกลึง 1"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-slate-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                เทมเพลตที่กำหนด (Assigned Template) *
              </label>
              <select
                value={formAssignedTemplateId}
                onChange={(e) => setFormAssignedTemplateId(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.machineModel})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ชั่วโมงทำงานสะสม (Cumulative Hours) *
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formCumulativeHours}
                onChange={(e) => setFormCumulativeHours(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono placeholder-slate-400"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              สถานะการตรวจเช็ก (PM Status)
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['NORMAL', 'DUE_SOON', 'OVERDUE'] as MachineStatus[]).map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setFormStatus(st)}
                  className={`min-h-[48px] py-2 px-3 rounded-lg text-xs font-bold border transition-all ${
                    formStatus === st
                      ? st === 'NORMAL'
                        ? 'bg-emerald-600 text-white border-emerald-500'
                        : st === 'DUE_SOON'
                        ? 'bg-amber-600 text-white border-amber-500'
                        : 'bg-rose-600 text-white border-rose-500'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsModalOpen(false)}
            >
              ยกเลิก
            </Button>
            <Button type="submit" variant="primary">
              {editingMachine ? 'บันทึกการแก้ไข' : 'เพิ่มเครื่องจักร'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Quick Add Hours Modal */}
      <Modal
        isOpen={Boolean(quickHoursMachine)}
        onClose={() => setQuickHoursMachine(null)}
        title={`เพิ่มชั่วโมงทำงาน: ${quickHoursMachine?.name}`}
        maxWidth="sm"
      >
        <div className="space-y-4 py-2">
          <p className="text-xs text-slate-600">
            ชั่วโมงปัจจุบัน: <strong className="text-amber-700 font-mono">{quickHoursMachine?.cumulativeHours} ชม.</strong>
          </p>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              จำนวนชั่วโมงที่ต้องการเพิ่ม (ชม.)
            </label>
            <input
              type="number"
              step="0.5"
              min="0.1"
              value={quickHoursToAdd}
              onChange={(e) => setQuickHoursToAdd(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-lg font-mono text-center text-slate-900 min-h-[48px] focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-4 gap-2">
            {['4', '8', '12', '24'].map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setQuickHoursToAdd(h)}
                className="py-2 text-xs font-mono font-bold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 min-h-[40px]"
              >
                +{h}h
              </button>
            ))}
          </div>

          <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setQuickHoursMachine(null)}>
              ยกเลิก
            </Button>
            <Button variant="primary" onClick={handleQuickAddHours}>
              ยืนยันเพิ่มชั่วโมง
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        title="ยืนยันการลบเครื่องจักร"
        maxWidth="sm"
      >
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-700">
            คุณแน่ใจหรือไม่ว่าต้องการลบเครื่องจักร <strong className="text-rose-600">{deleteConfirmId}</strong> ออกจากระบบ?
          </p>
          <p className="text-xs text-slate-500">
            การดำเนินการนี้ไม่สามารถยกเลิกได้ และอาจส่งผลต่อการบันทึกประวัติการตรวจเช็ก
          </p>
          <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
              ยกเลิก
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirmId && handleDeleteMachine(deleteConfirmId)}
            >
              ลบเครื่องจักร
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
